import { Button, Icon, Notice, PageHead, Panel, PanelHead, PhaseTag } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { NewCaseDialog } from '../console/NewCase';
import { type CaseRowView, CaseTable, Legend, QUEUE_CHIPS, type Queue } from './CaseTable';
import s from './cases.module.css';

export interface CaseListResponse {
  rows: CaseRowView[];
  total: number;
  counts: Record<Queue, number>;
}

export const casesKey = (queue: string, q: string, qr: boolean) => ['cases', queue, q, qr] as const;

export function QueueChips({
  counts,
  current,
  onPick,
}: {
  counts: Partial<Record<Queue, number>>;
  current: Queue;
  onPick: (q: Queue) => void;
}) {
  return (
    <section className={s.queues} aria-label="Work queues">
      {QUEUE_CHIPS.map((c) => (
        <button
          key={c.key}
          type="button"
          className={s.chip}
          aria-pressed={current === c.key}
          onClick={() => onPick(c.key)}
        >
          {c.dot && <span className={s.dotm} style={{ background: c.dot }} />}
          <span className={s.n}>{counts[c.key] ?? 0}</span> {c.label}
        </button>
      ))}
      <span className={s.chip} aria-disabled="true" title="Consultation minutes arrive in Phase 3">
        <span className={s.dotm} style={{ background: 'var(--bad)' }} />
        Awaiting minutes <PhaseTag phase="P3" />
      </span>
    </section>
  );
}

const TITLES: Partial<Record<Queue, [string, string]>> = {
  panel: ['Panels', 'Cases released to physicians and waiting for the panel to fill.'],
  week: ['Consultations', 'Scheduled consultations in the next seven days.'],
};

/**
 * The case list (CON-01 to CON-04): search, work queues, quality-review filter. Filters live
 * in the URL, so they hold across navigation and a link to a queue can be shared.
 */
export function CasesPage({
  me,
  fixedQueue,
  current = 'cases',
}: {
  me: Me;
  fixedQueue?: Queue;
  current?: string;
}) {
  const [params, setParams] = useSearchParams();
  const queue = (fixedQueue ?? (params.get('queue') as Queue | null) ?? 'all') as Queue;
  const q = params.get('q') ?? '';
  const qr = params.get('qr') === '1';
  const [text, setText] = useState(q);
  const [creating, setCreating] = useState(false);
  const res = useQuery({
    queryKey: casesKey(queue, q, qr),
    queryFn: () =>
      api<CaseListResponse>(
        `/staff/cases?${new URLSearchParams({ queue, q, qualityReview: String(qr), limit: '100' })}`,
      ),
  });
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };
  const [title, lead] =
    TITLES[queue] && fixedQueue
      ? TITLES[queue]!
      : ['Cases', 'Every case since intake, newest first. Pick a queue or search.'];
  const label = queue === 'all' ? 'All cases' : (QUEUE_CHIPS.find((c) => c.key === queue)?.label ?? 'Closed');

  return (
    <ConsoleLayout me={me} current={current}>
      <PageHead
        title={title}
        when={lead}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Icon.Plus />
            New case
          </Button>
        }
      />
      {!fixedQueue && res.data && (
        <div className={s.tools}>
          <QueueChips
            counts={res.data.counts}
            current={queue}
            onPick={(k) => set({ queue: k === queue ? null : k })}
          />
          <button
            type="button"
            className={s.chip}
            aria-pressed={queue === 'all'}
            onClick={() => set({ queue: null })}
          >
            <span className={s.n}>{res.data.counts.all}</span> All
          </button>
          <button
            type="button"
            className={s.chip}
            aria-pressed={queue === 'closed'}
            onClick={() => set({ queue: 'closed' })}
          >
            <span className={s.n}>{res.data.counts.closed}</span> Closed
          </button>
        </div>
      )}
      <form
        className={s.tools}
        onSubmit={(e) => {
          e.preventDefault();
          set({ q: text.trim() || null });
        }}
      >
        <input
          className="input"
          style={{ maxWidth: 420 }}
          aria-label="Search cases"
          placeholder="Reference, patient name or email"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" variant="sec">
          Search
        </Button>
        <label className="check">
          <input type="checkbox" checked={qr} onChange={(e) => set({ qr: e.target.checked ? '1' : null })} />
          Quality review only
        </label>
      </form>
      {res.isError && <Notice tone="bad">{errorMessage(res.error)}</Notice>}
      <Panel aria-label="Cases">
        <PanelHead
          title={label}
          sub={res.data ? `${res.data.total} ${res.data.total === 1 ? 'case' : 'cases'}` : ''}
        />
        {res.data && res.data.rows.length > 0 && (
          <CaseTable rows={res.data.rows} tz={me.timezone} now={new Date()} />
        )}
        {res.data && res.data.rows.length === 0 && <div className={s.empty}>No cases match.</div>}
        <Legend />
      </Panel>
      <NewCaseDialog open={creating} onOpenChange={setCreating} />
    </ConsoleLayout>
  );
}
